import EnterpriseLogService from './enterpriseLogService';

/**
 * Safe wrapper for EnterpriseLogService that handles JSON parsing errors
 * and provides robust error handling for production environments
 */
class SafeEnterpriseLogService {
  /**
   * Safely parse JSON response with comprehensive error handling
   */
  static safeParseResponse(responseData) {
    try {
      // Validate response structure
      if (!responseData) {
        console.error('Invalid response data from server');
        return {
          status: 0,
          message: 'Invalid response from server',
          data: null
        };
      }
      
      // If it's already an object, return it
      if (typeof responseData === 'object') {
        return responseData;
      }
      
      // If it's a string, try to parse it
      if (typeof responseData === 'string') {
        try {
          const parsedData = JSON.parse(responseData);
          return parsedData;
        } catch (parseError) {
          console.error('Failed to parse response data:', parseError);
          return {
            status: 0,
            message: 'Invalid JSON response from server',
            data: null
          };
        }
      }
      
      // If it's something else, wrap it
      return {
        status: 1,
        data: responseData
      };
    } catch (error) {
      console.error('Error in safeParseResponse:', error);
      return {
        status: 0,
        message: 'Failed to process server response',
        data: null
      };
    }
  }

  /**
   * Handle API errors with comprehensive error detection
   */
  static handleApiError(error, fallbackData = null) {
    console.error('API Error:', error);
    
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const responseText = error.response.data;
      
      // Check if response is HTML (error page) instead of JSON
      if (typeof responseText === 'string' && responseText.includes('<html')) {
        console.error('Server returned HTML error page instead of JSON');
        return {
          status: 0,
          message: 'Server error - please try again later',
          data: fallbackData
        };
      }
      
      if (status === 401) {
        return {
          status: 0,
          message: 'Authentication required',
          data: fallbackData
        };
      }
      
      if (status === 500) {
        return {
          status: 0,
          message: 'Server error - please try again later',
          data: fallbackData
        };
      }
    }
    
    // Network or other errors
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      return {
        status: 0,
        message: 'Network error - please check your connection',
        data: fallbackData
      };
    }
    
    // Default fallback
    return {
      status: 0,
      message: 'Failed to fetch data - please try again',
      data: fallbackData
    };
  }

  /**
   * Safe wrapper for getLogs with comprehensive error handling
   */
  static async getLogs(params = {}) {
    try {
      const responseData = await EnterpriseLogService.getLogs(params);
      return this.safeParseResponse(responseData);
    } catch (error) {
      console.error('Error fetching logs:', error);
      return this.handleApiError(error, { 
        data: [], 
        meta: { total: 0, current_page: 1, last_page: 1 } 
      });
    }
  }

  /**
   * Safe wrapper for getStatistics with comprehensive error handling
   */
  static async getStatistics(params = {}) {
    try {
      const responseData = await EnterpriseLogService.getStatistics(params);
      return this.safeParseResponse(responseData);
    } catch (error) {
      return this.handleApiError(error, {
        total_logs: 0,
        critical_events: 0,
        security_events: 0,
        today_logs: 0,
        active_users: 0
      });
    }
  }

  /**
   * Safe wrapper for getFilterOptions with comprehensive error handling
   */
  static async getFilterOptions() {
    try {
      const responseData = await EnterpriseLogService.getFilterOptions();
      return this.safeParseResponse(responseData);
    } catch (error) {
      return this.handleApiError(error, {
        levels: [],
        channels: [],
        categories: [],
        components: []
      });
    }
  }

  /**
   * Safe wrapper for getAuditTrail with comprehensive error handling
   */
  static async getAuditTrail(params = {}) {
    try {
      const responseData = await EnterpriseLogService.getAuditTrail(params);
      
      // Minimal debugging - only log if there's an issue
      if (typeof responseData === 'string' && !responseData.trim()) {
        console.warn('Empty response received from audit trail API');
      }
      
      return this.safeParseResponse(responseData);
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      return this.handleApiError(error, { 
        data: [], 
        meta: { total: 0, current_page: 1, last_page: 1 } 
      });
    }
  }

  /**
   * Safe wrapper for getSecurityEvents with comprehensive error handling
   */
  static async getSecurityEvents(params = {}) {
    try {
      const responseData = await EnterpriseLogService.getSecurityEvents(params);
      return this.safeParseResponse(responseData);
    } catch (error) {
      return this.handleApiError(error, { 
        data: [], 
        meta: { total: 0, current_page: 1, last_page: 1 } 
      });
    }
  }

  /**
   * Safe wrapper for getPerformanceMetrics with comprehensive error handling
   */
  static async getPerformanceMetrics(params = {}) {
    try {
      const responseData = await EnterpriseLogService.getPerformanceMetrics(params);
      return this.safeParseResponse(responseData);
    } catch (error) {
      return this.handleApiError(error, { 
        data: [], 
        meta: { total: 0, current_page: 1, last_page: 1 } 
      });
    }
  }

  /**
   * Safe wrapper for getRecentActivity with comprehensive error handling
   */
  static async getRecentActivity(hours = 24) {
    try {
      const responseData = await EnterpriseLogService.getRecentActivity(hours);
      return this.safeParseResponse(responseData);
    } catch (error) {
      return this.handleApiError(error, { 
        data: [], 
        meta: { total: 0, current_page: 1, last_page: 1 } 
      });
    }
  }

  /**
   * Safe wrapper for searchLogs with comprehensive error handling
   */
  static async searchLogs(searchTerm, params = {}) {
    try {
      const responseData = await EnterpriseLogService.searchLogs(searchTerm, params);
      return this.safeParseResponse(responseData);
    } catch (error) {
      return this.handleApiError(error, { 
        data: [], 
        meta: { total: 0, current_page: 1, last_page: 1 } 
      });
    }
  }

  /**
   * Safe wrapper for exportLogs with comprehensive error handling
   */
  static async exportLogs(params = {}, format = 'csv') {
    try {
      const response = await EnterpriseLogService.exportLogs(params, format);
      return response;
    } catch (error) {
      console.error('Export error:', error);
      throw error; // Re-throw export errors as they need special handling
    }
  }

  // Delegate utility methods directly
  static getLevelColor(level) {
    return EnterpriseLogService.getLevelColor(level);
  }

  static getLevelPriority(level) {
    return EnterpriseLogService.getLevelPriority(level);
  }

  static formatContext(context) {
    try {
      return EnterpriseLogService.formatContext(context);
    } catch (error) {
      console.error('Error formatting context:', error);
      return {};
    }
  }
}

export default SafeEnterpriseLogService;
